import { Box, Stack, Typography } from '@vapor/material'
import { RichGraphQLDataGrid } from 'hic-components'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BaseCustomerLookup, { BaseCustomerLookup as BaseCustomerLookupType } from '../../customer-lookup/base/base-customer-lookup'
import {
  CustomerType,
  HistoricalCustomerResearchCustomerFragment,
  Note,
  ReservationQueryFilter,
  HistoricalCustomerResearchDocument,
  HistoricalCustomerResearchQuery,
  HistoricalCustomerResearchQueryVariables,
  SimpleCustomersLookupDocument,
  HistoricalCustomerReservedResourceResearchDocument,
  HistoricalCustomerReservedResourceResearchQuery,
  HistoricalCustomerReservedResourceResearchQueryVariables,
  ReservedResourceQueryFilter,
  SimpleCustomerLookupFragment,
} from 'src/api/api.generated'
import { differenceInDays } from 'date-fns'
import { GridColDef } from '@mui/x-data-grid'
import RangeDateCell from '../../range-date-cell/range-date-cell'
import { HicChip } from '../../hic-chip/hic-chip'
import { faBuilding, faHandshakeAngle, faHashtag, faTag, faUser } from '@fortawesome/pro-regular-svg-icons'
import NotesCell from 'src/app/pages/common/dashboard/components/notes-cell'
import { Currency } from 'src/api/currency'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers } from '@fortawesome/pro-light-svg-icons'
import { HiCIcon } from '../../icons/icon'
import { SimpleCustomerLookupOption } from '../../customer-lookup/option/simple-customer-lookup-option'
import { useHicCommonDataGridProps } from '../../hic-vapor/datagrid/datagrid'

type HistoricalCustomerResearchGridProps = {
  tenantId: string
  propertyId: string
  initialCustomer: BaseCustomerLookupType
  hideCustomerFilter?: boolean
}

type Query = HistoricalCustomerResearchQuery | HistoricalCustomerReservedResourceResearchQuery
type QueryVariables = HistoricalCustomerResearchQueryVariables | HistoricalCustomerReservedResourceResearchQueryVariables
type QueryFilter = ReservationQueryFilter | ReservedResourceQueryFilter

type HistoricalCustomerResearchGridList = {
  start: Date | undefined
  end: Date | undefined
  mainGuest: HistoricalCustomerResearchCustomerFragment
  company: HistoricalCustomerResearchCustomerFragment | undefined
  agency: HistoricalCustomerResearchCustomerFragment | undefined
  guestOccupancyNames: string[]
  notes: Note[]
  group: string | undefined
  isGroup: boolean
  id: string
  averageDailyPrice: number
  totalPax: number
  currency: Currency
  code: string
}

function isReservationQuery(query: Query): query is HistoricalCustomerResearchQuery {
  return 'reservations' in query
}

function isReservationFilter(filter: QueryFilter | undefined): filter is ReservationQueryFilter {
  return Boolean(filter && 'customerId' in filter)
}

export default function HistoricalCustomerResearchGrid({ initialCustomer, tenantId, propertyId, hideCustomerFilter }: HistoricalCustomerResearchGridProps) {
  const { t } = useTranslation()
  const [customerType, setCustomerType] = useState<CustomerType | null>(initialCustomer.type)
  const usingCustomerFilter = Boolean(customerType)
  const { hicCommonDataGridPremiumProps } = useHicCommonDataGridProps()

  const variables = useCallback(
    ({ filter, pagination }) => {
      if (customerType === CustomerType.COMPANY) {
        return {
          filter: {
            tenantId: {
              eq: tenantId,
            },
            propertyId,
            customerId: initialCustomer?.id ?? undefined,
            ...filter,
          },
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 10,
        }
      } else {
        return {
          filter: {
            tenantId: {
              eq: tenantId,
            },
            propertyId: {
              eq: propertyId,
            },
            guestId: initialCustomer?.id ?? undefined,
            ...filter,
          },
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 10,
        }
      }
    },
    [tenantId, propertyId, initialCustomer?.id ?? '', customerType],
  )

  const getQueryResponse = useCallback((response: Query | undefined): HistoricalCustomerResearchGridList[] => {
    if (!response) return []

    const reservations = isReservationQuery(response) ? response?.reservations ?? [] : (response?.reservedResources ?? []).map((reservedResource) => reservedResource.reservation)

    return reservations
      .map((reservation) => {
        let start: Date | undefined = undefined
        let end: Date | undefined = undefined
        if (customerType === CustomerType.COMPANY) {
          start = (reservation.reservedResources ?? [])?.reduce((a, b) => (a.start.getTime() < b.start.getTime() ? a : b))?.start ?? undefined
          end = (reservation.reservedResources ?? [])?.reduce((a, b) => (a.end > b.end ? a : b))?.end ?? undefined
        } else {
          let guestOccupancyWithCustomer = undefined
          for (const reservedResource of reservation.reservedResources ?? []) {
            for (const guestOccupancy of reservedResource.guestOccupancies ?? []) {
              if (guestOccupancy.guest.id === initialCustomer.id) {
                guestOccupancyWithCustomer = guestOccupancy
              }
            }
          }

          if (guestOccupancyWithCustomer) {
            start = guestOccupancyWithCustomer.start
            end = guestOccupancyWithCustomer.end
          }
        }

        return {
          id: reservation.id,
          start,
          end,
          mainGuest: reservation.reference.mainGuest,
          company: reservation.reference.customerCompany ?? undefined,
          agency: reservation.reference.agency ?? undefined,
          notes: reservation.notes ?? [],
          group: reservation.group ?? '',
          isGroup: reservation.isGroup ?? false,
          code: reservation.reservationCode,
          guestOccupancyNames: (reservation.reservedResources ?? []).flatMap((reservedResource) => reservedResource.guestOccupancies?.map((guestOccupancy) => guestOccupancy.guest.name) ?? []),
          totalPax: reservation.reservedResources?.reduce((totalPax, reservedResource) => totalPax + (reservedResource.adults + reservedResource.children), 0) ?? 0,
          currency: reservation.reservedResources?.[0]?.productions?.[0]?.currency ?? 'EUR',
          averageDailyPrice:
            reservation.reservedResources?.reduce((averageDailyPrice, reservedResource) => {
              const reservedResourcePrice = (reservedResource.productions ?? [])
                ?.filter((production) => production.chargeCategory.stayIncluded)
                .map((production) => production.amount)
                .reduce((a, b) => a + b, 0)
              averageDailyPrice += reservedResourcePrice / (end && start ? differenceInDays(end, start) : 1)

              return averageDailyPrice
            }, 0) ?? 0,
        }
      })
      .sort((a, b) => (b.start && a.start ? b.start?.getTime() - a.start?.getTime() : b.start ? 1 : a.start ? -1 : 0))
  }, [])

  const columns = useColumns()

  return (
    <>
      {!usingCustomerFilter && <Typography>{t('pms.booking.reservation_new.historical_customer_research.grid.no_customer_filter')}</Typography>}
      <RichGraphQLDataGrid<Query, HistoricalCustomerResearchGridList, QueryVariables, QueryFilter>
        {...hicCommonDataGridPremiumProps}
        query={customerType === CustomerType.COMPANY ? HistoricalCustomerResearchDocument : HistoricalCustomerReservedResourceResearchDocument}
        data={getQueryResponse}
        columns={columns}
        pagination={false}
        sx={{
          height: hideCustomerFilter ? 600 : 250,
          backgroundColor: 'white',
          display: !usingCustomerFilter ? 'none' : 'flex',
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 'normal',
          },
          '& .MuiDataGrid-columnHeader': {
            // Forced to use important since overriding inline styles
            height: 'unset !important',
          },
          '& .MuiDataGrid-columnHeaders': {
            // Forced to use important since overriding inline styles
            maxHeight: '168px !important',
          },
        }}
        variables={variables}
        getRowHeight={() => 'auto'}
        filters={
          hideCustomerFilter
            ? undefined
            : [
                {
                  primary: true,
                  label: t('pms.booking.reservation_new.historical_customer_research.customer'),
                  serialization: (value) => {
                    return isReservationFilter(value) ? value.customerId ?? undefined : value.guestId ?? undefined
                  },
                  reset: (value) => ({ ...value, customerId: undefined }),
                  component: ({ value, onChange }) => {
                    const valueId = !value ? undefined : isReservationFilter(value) ? value.customerId : value.guestId
                    const valueLookup = !usingCustomerFilter ? null : valueId ? { id: valueId } : initialCustomer ? initialCustomer : null

                    return (
                      <Box sx={{ width: 200 }}>
                        <BaseCustomerLookup<SimpleCustomerLookupFragment>
                          size="small"
                          value={valueLookup}
                          queryDocument={SimpleCustomersLookupDocument}
                          minSearchParameterLenght={2}
                          renderOption={(option) => <SimpleCustomerLookupOption option={option} showEmail={true} showPhoneNumber={true} showTags={true} showRelatedCompany={true} />}
                          onChange={(customerToLook) => {
                            setCustomerType(customerToLook?.type ?? null)

                            onChange(
                              customerToLook?.type === CustomerType.COMPANY
                                ? {
                                    tenantId: {
                                      eq: tenantId,
                                    },
                                    propertyId,
                                    customerId: customerToLook?.id ? customerToLook.id : undefined,
                                  }
                                : {
                                    tenantId: {
                                      eq: tenantId,
                                    },
                                    propertyId: {
                                      eq: propertyId,
                                    },
                                    guestId: customerToLook?.id ? customerToLook.id : undefined,
                                  },
                            )
                          }}
                          noOptionsText={<i>{t('pms.components.customer_lookup.name_input_tip')}</i>}
                        />
                      </Box>
                    )
                  },
                },
              ]
        }
      />
    </>
  )
}

function useColumns(): GridColDef[] {
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency()

  return [
    {
      field: 'start',
      headerName: t('pms.booking.reservation_new.historical_customer_research.grid.date'),
      disableColumnMenu: true,
      width: 120,
      renderCell: (params) => {
        const start = params.row.start
        const end = params.row.end
        if (!start || !end) return null

        return <RangeDateCell start={start} end={end} />
      },
    },
    {
      field: 'customer',
      headerName: t('pms.booking.reservation_new.historical_customer_research.grid.booker'),
      disableColumnMenu: true,
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const mainCustomer = params.row.mainGuest
        const company = params.row.company
        const agency = params.row.agency
        const group = params.row.group
        const isGroup = params.row.isGroup

        return (
          <Stack>
            <Box>{mainCustomer.name}</Box>
            {isGroup && group && (
              <Box>
                <FontAwesomeIcon icon={faUsers} /> {group}
              </Box>
            )}
            {company && (
              <Box>
                <FontAwesomeIcon icon={faBuilding} /> {company.name}
              </Box>
            )}
            {agency && (
              <Box>
                <FontAwesomeIcon icon={faHandshakeAngle} /> {agency.name}
              </Box>
            )}
          </Stack>
        )
      },
    },
    {
      field: 'guestOccupancyNames',
      headerName: t('pms.booking.reservation_new.historical_customer_research.grid.guest'),
      disableColumnMenu: true,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as HistoricalCustomerResearchGridList
        const guestOccupancyNames: string[] = row.guestOccupancyNames.slice(0, 3)
        const hasMoreGuests = row.guestOccupancyNames.length > 3

        return (
          <Stack>
            {guestOccupancyNames.map((name, index) => (
              <Box key={index}>{name},</Box>
            ))}
            {hasMoreGuests && <Box>...</Box>}
          </Stack>
        )
      },
    },
    {
      field: 'info',
      headerName: t('pms.booking.reservation_new.historical_customer_research.grid.info'),
      disableColumnMenu: true,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const totalPax = params.row.totalPax
        const mainCustomer = params.row.mainGuest
        const company = params.row.company
        const agency = params.row.agency
        const notes = params.row.notes

        let tags = []
        if (mainCustomer.tags?.length > 0) tags.push(...mainCustomer.tags)
        if (company?.tags?.length > 0) tags.push(...company.tags)
        if (agency?.tags?.length > 0) tags.push(...agency.tags)

        tags = tags.filter((tag, index, self) => self.findIndex((t) => t.id === tag.id) === index)

        return (
          <Stack alignItems={'start'} rowGap={0.5} minWidth={0} marginY={0.5} sx={{ '& > .MuiStack-root': { rowGap: 0.5 } }}>
            <Stack direction="row" gap={0.5}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HiCIcon>
                  <FontAwesomeIcon icon={faHashtag} />
                </HiCIcon>
                {params.row.code}
              </Box>
              <HicChip variant={'light'} text={String(totalPax)} icon={faUser} />
            </Stack>
            {tags.map((tag) => (
              <HicChip key={tag.id} variant={'light'} text={tag.label} color={tag.color} icon={faTag} />
            ))}
            <NotesCell notes={notes} />
          </Stack>
        )
      },
    },
    {
      field: 'averageDailyPrice',
      headerName: t('pms.booking.reservation_new.historical_customer_research.grid.average_daily_price'),
      disableColumnMenu: true,
      width: 80,
      sortable: false,
      renderCell: (params) => formatCurrency(params.row.averageDailyPrice, params.row.currency),
    },
  ]
}
